import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { OrderStatusBadge } from "./OrderStatusBadge";
import { printCustomerReceipt } from "@/lib/order-printing";
import { printDeliveryReceipt } from "@/lib/delivery-printing";
import { toast } from "sonner";
import type { OrderWithDetails } from "@/hooks/useOrdersList";
import { MapPin, User, Clock, CreditCard, FileText, Printer, Loader2, Phone, Truck } from "lucide-react";
import { useFiscalConfig } from "@/hooks/useFiscalConfig";
import { useNfceEmission } from "@/hooks/useNfceEmission";
import { NfceEmissionDialog } from "@/components/fiscal/NfceEmissionDialog";
import { useRestaurantContext } from "@/contexts/RestaurantContext";

interface OrderDetailsDialogProps {
  order: OrderWithDetails | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const orderTypeLabels: Record<string, string> = {
  table: "Mesa",
  tab: "Comanda",
  counter: "Balcão",
  delivery: "Delivery",
};

export function OrderDetailsDialog({
  order,
  open,
  onOpenChange,
}: OrderDetailsDialogProps) {
  const { config: fiscalConfig } = useFiscalConfig();
  const { emitNfce, isEmitting: isEmittingNfce, lastResult: nfceResult } = useNfceEmission();
  const [nfceDialogOpen, setNfceDialogOpen] = useState(false);
  const { activeRestaurant } = useRestaurantContext();

  if (!order) return null;

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  const handlePrintReceipt = async () => {
    if (!order) return;
    try {
      await printCustomerReceipt({
        orderNumber: order.order_number,
        items: order.items.map((item) => ({
          name: item.product_name,
          quantity: item.quantity,
          unit_price: item.unit_price,
          total_price: item.total_price,
        })),
        subtotal: Number(order.subtotal),
        discount: Number(order.discount) || 0,
        total: Number(order.total),
        paymentMethod: order.payments[0]?.payment_method?.name || "Não informado",
        customerName: order.customer_name || undefined,
      });
    } catch (error) {
      console.error("Error printing receipt:", error);
      toast.error("Erro ao imprimir via do cliente");
    }
  };

  const handlePrintDeliveryReceipt = async () => {
    if (!order || !order.delivery_address) return;
    try {
      await printDeliveryReceipt(order.id, activeRestaurant?.id || "");
      toast.success("Via do entregador enviada!");
    } catch (error) {
      console.error("Error printing delivery receipt:", error);
      toast.error("Erro ao imprimir via do entregador");
    }
  };

  const handleEmitNfce = async () => {
    if (!order) return;
    setNfceDialogOpen(true);
    
    // Mapear pagamentos para o formato esperado pela API
    const paymentMethodNameToType = (name: string | undefined): string => {
      if (!name) return 'outros';
      const lower = name.toLowerCase();
      if (lower.includes('dinheiro')) return 'dinheiro';
      if (lower.includes('pix')) return 'pix';
      if (lower.includes('crédito')) return 'cartao_credito';
      if (lower.includes('débito')) return 'cartao_debito';
      if (lower.includes('cartao') || lower.includes('cartão')) return 'cartao_credito';
      return 'outros';
    };
    
    await emitNfce(order.id, order.payments.map(p => ({
      method: paymentMethodNameToType(p.payment_method?.name),
      amount: Math.round(p.amount * 100)
    })));
  };

  // Mostrar botão NFC-e apenas para pedidos finalizados (completed/paid)
  const canEmitNfce = fiscalConfig?.integrator === 'acbr_monitor' 
    && ['completed', 'paid'].includes(order.status);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>Pedido #{order.order_number}</span>
              <OrderStatusBadge status={order.status} />
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Order Info */}
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Clock className="h-4 w-4" />
                <span>
                  {format(new Date(order.created_at), "dd/MM/yyyy 'às' HH:mm", {
                    locale: ptBR,
                  })}
                </span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                {order.delivery_address ? (
                  <>
                    <Truck className="h-4 w-4 text-blue-600" />
                    <span className="text-blue-600 font-medium">Delivery</span>
                  </>
                ) : (
                  <>
                    <MapPin className="h-4 w-4" />
                    <span>
                      {orderTypeLabels[order.order_type]}
                      {order.table_number && ` ${order.table_number}`}
                      {order.tab_number && ` ${order.tab_number}`}
                    </span>
                  </>
                )}
              </div>
              {order.customer_name && (
                <div className="flex items-center gap-2 text-muted-foreground col-span-2">
                  <User className="h-4 w-4" />
                  <span>{order.customer_name}</span>
                </div>
              )}
              {(order as any).customer_phone && (
                <div className="flex items-center gap-2 text-muted-foreground col-span-2">
                  <Phone className="h-4 w-4" />
                  <span>{(order as any).customer_phone}</span>
                </div>
              )}
            </div>

            {/* Delivery Address */}
            {order.delivery_address && (
              <>
                <Separator />
                <div>
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <MapPin className="h-4 w-4" />
                    Endereço de Entrega
                  </h4>
                  <p className="text-sm text-muted-foreground">{order.delivery_address}</p>
                </div>
              </>
            )}

            <Separator />

            {/* Items */}
            <div>
              <h4 className="font-medium mb-2">Itens do Pedido</h4>
              <div className="space-y-2">
                {order.items.map((item) => (
                  <div key={item.id} className="flex justify-between text-sm">
                    <div className="flex-1">
                      <span className="font-medium">{item.quantity}x</span>{" "}
                      {item.product_name}
                      {item.notes && (
                        <p className="text-xs text-muted-foreground ml-6">
                          {item.notes}
                        </p>
                      )}
                    </div>
                    <span>{formatCurrency(item.total_price)}</span>
                  </div>
                ))}
              </div>
            </div>

            <Separator />

            {/* Payments */}
            <div>
              <h4 className="font-medium mb-2 flex items-center gap-2">
                <CreditCard className="h-4 w-4" />
                Pagamentos
              </h4>
              <div className="space-y-1">
                {order.payments.length > 0 ? (
                  order.payments.map((payment) => (
                    <div key={payment.id} className="flex justify-between text-sm">
                      <span>{payment.payment_method?.name || "Método removido"}</span>
                      <span>{formatCurrency(payment.amount)}</span>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground">
                    Sem informação de pagamento
                  </p>
                )}
              </div>
            </div>

            {order.notes && (
              <>
                <Separator />
                <div>
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Observações
                  </h4>
                  <p className="text-sm text-muted-foreground">{order.notes}</p>
                </div>
              </>
            )}

            <Separator />

            {/* Totals */}
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Subtotal</span>
                <span>{formatCurrency(Number(order.subtotal))}</span>
              </div>
              {Number(order.discount) > 0 && (
                <div className="flex justify-between text-destructive">
                  <span>Desconto</span>
                  <span>-{formatCurrency(Number(order.discount))}</span>
                </div>
              )}
              {Number((order as any).delivery_fee) > 0 && (
                <div className="flex justify-between text-muted-foreground">
                  <span>Taxa de Entrega</span>
                  <span>{formatCurrency(Number((order as any).delivery_fee))}</span>
                </div>
              )}
              {Number(order.payment_fee) > 0 && (
                <div className="flex justify-between text-muted-foreground">
                  <span>Taxa</span>
                  <span>{formatCurrency(Number(order.payment_fee))}</span>
                </div>
              )}
              <div className="flex justify-between font-bold text-base pt-2 border-t">
                <span>Total</span>
                <span>{formatCurrency(Number(order.total))}</span>
              </div>
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            {canEmitNfce && (
              <Button 
                variant="outline" 
                onClick={handleEmitNfce}
                disabled={isEmittingNfce}
                className="gap-2"
              >
                {isEmittingNfce ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <FileText className="h-4 w-4" />
                )}
                Emitir NFC-e
              </Button>
            )}
            {order.delivery_address && (
              <Button variant="outline" onClick={handlePrintDeliveryReceipt}>
                <Truck className="h-4 w-4 mr-2" />
                Via Entregador
              </Button>
            )}
            <Button variant="outline" onClick={handlePrintReceipt}>
              <Printer className="h-4 w-4 mr-2" />
              Reimprimir Via
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* NFC-e Emission Dialog */}
      <NfceEmissionDialog
        open={nfceDialogOpen}
        onOpenChange={setNfceDialogOpen}
        isEmitting={isEmittingNfce}
        result={nfceResult}
        onRetry={handleEmitNfce}
      />
    </>
  );
}
